import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import styled from "styled-components";
import { BlurNotePill } from "@/_self_managed/SM_Dashboard/comps/BlurNoteOverlay";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import { colors2 } from "oolib";
import { UI_BODY_SEMIBOLD_SM_DF, UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";
import { Info } from "@phosphor-icons/react";
import TooltipV2 from "@/components/UI/Tooltip";
import { TooltipContent } from "@/components/dataviz/CustomTooltip";
import { useTrackMousePosition } from "@/components/dataviz/utils/useTrackMousePosition";
import { _base_linechart_ui_configs } from "@/components/dataviz/LineChartV2/_base_linechart_ui_configs";
import { DashboardWidgetShell } from "@/_self_managed/SM_Dashboard/comps/DashboardWidgetShell";
import { useSignalCategories } from "../SignalCategoriesContext";
import { CONFIG } from "./CONFIG";
import { plotX, unplotX, PLOT_X_MAX, PLOT_X_TICKS } from "./xScale";
import { resolveZones } from "./zoneStyles";
import { momentumText, severityText, sourcesText } from "./format";

/**
 * The field — severity (y) against the momentum index (x), one bubble per
 * sub-topic, sized by observation count, coloured by signal category.
 *
 * Built on recharts like the rest of dataviz: a ScatterChart whose six
 * zones are ReferenceAreas (the high-severity line at CONFIG.SEVERITY_HIGH
 * splits the rows, the steady band at index ±1 splits the columns), axes
 * styled with the line chart's tokens, and the shared controlled TooltipV2
 * + TooltipContent driven by the dataviz mouse-tracking hook — the same
 * hover wiring the heatmap uses.
 *
 * Sub-topics whose momentum could not be computed sit hollow on the x = 0
 * line so they are still visible by severity.
 *
 * `fill`: instead of the fixed HEIGHT, the chart takes whatever height its
 * wrapper is given (the parent sizes the row; the wrapper is measured with a
 * ResizeObserver so the label maths reads real pixels).
 *
 * `focusIds`: the bubbles that matter right now (the act-first list). They
 * draw at full strength with their white edge; every other bubble fades to
 * 0.4 and loses the edge, so the field reads as "these eight, and context".
 * Unlike `highlightIds` (an insight in focus) it does not touch the labels.
 *
 * `blurNote` (+ `blurNoteIcon`): one period of data. Severity is still a
 * real reading, momentum is not, so the bubbles and the severity axis stay
 * as they are while everything that reads momentum — the six zones, their
 * labels, the column lines, the x axis — sits blurred, with the note on the
 * axis band saying what fills them in.
 */

// The single-period note, centred on the blurred x-axis band (blurNote).
const NoteOnAxis = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
`;

const HEIGHT = 420;
const MARGIN = { top: 16, right: 24, bottom: 8, left: 4 };
// recharts reserves ~30px for the x-axis band; what's left is the plot.
// Only used to translate the label-stacking gap (px) into data units.
const plotHeightPx = (height) => height - MARGIN.top - MARGIN.bottom - 30;
const LABEL_GAP_PX = 13;
// Room past the plotted cap so a bubble sitting on the edge isn't clipped;
// the zones extend to the same edge so the tint runs the full plot. Plot
// units (see xScale.js), not index units.
const X_OVERSHOOT = 0.18;
const TICK_FONT_FAMILY =
  "'DM Sans', 'Noto Sans Devanagari', 'Noto Sans Gujarati', 'Noto Sans Oriya', 'Noto Sans Telugu', sans-serif";

const Wrap = styled.div`
  position: relative;
  width: 100%;
  ${({ $fill }) => $fill && "flex: 1; min-height: 0;"}

  /* One period of data: the parts of the field that read momentum blur out
     (blurNote); bubbles and the severity axis stay crisp. */
  ${({ $momentumBlurred }) =>
    $momentumBlurred &&
    `
    .recharts-reference-area,
    .recharts-xAxis,
    .momentum-line {
      filter: blur(3px);
      opacity: 0.6;
    }
  `}

  /* Hovering one bubble quietens the rest — same idea as the line chart's
     dim-the-others on hover. */
  &:hover .triage-dot {
    opacity: 0.4;
  }
  .triage-dot:hover {
    opacity: 1;
  }
  .triage-dot {
    cursor: pointer;
    transition: opacity 0.12s ease;
  }
  /* Outside the act-first set: context, not the point. Hovering still
     brings one up (the :hover rule below wins by order). */
  .triage-dot.triage-faded {
    opacity: 0.3;
  }
  .triage-dot.triage-faded:hover {
    opacity: 1;
  }
  /* An insight in focus: everything it is not about recedes. Beats the
     hover dimming above by specificity. */
  .triage-dot.triage-dim,
  &:hover .triage-dot.triage-dim {
    opacity: 0.12;
  }
  .triage-label {
    pointer-events: none;
    font-family: ${TICK_FONT_FAMILY};
  }
`;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// Bubble area ∝ observations, scaled against the LARGEST node on the field so
// the biggest bubble is always R_MAX px whatever the tenant's volumes — an
// absolute px-per-observation rule drew 100px bubbles on a 70k-observation
// corpus and 12px ones on a 300-observation corpus.
const R_MIN = 5;
const R_MAX = 22;
const radiusOf = (n, maxN) => R_MIN + (R_MAX - R_MIN) * Math.sqrt(Math.max(1, n) / Math.max(1, maxN));
// A zone's name in its corner, with an info icon that explains the zone on
// hover. Rendered as HTML inside the SVG (foreignObject) so the shared
// TooltipV2 can attach to the icon; recharts hands us the zone's pixel box
// via `viewBox`.
const ZoneTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

const ZONE_TAG_W = 200;
const ZONE_TAG_H = 18;

const ZoneLabel = ({ viewBox, zone, atBottom }) => {
  const zones = resolveZones(useSignalCategories().prioritiesCopy);
  if (!viewBox) return null;
  const { x, y, width, height } = viewBox;
  return (
    <foreignObject
      x={x + 8}
      y={atBottom ? y + height - ZONE_TAG_H - 6 : y + 6}
      width={Math.min(ZONE_TAG_W, Math.max(0, width - 8))}
      height={ZONE_TAG_H}
      style={{ overflow: "visible" }}
    >
      <ZoneTag>
        <UI_CAPTION_SEMIBOLD_DF style={{ color: colors2.grey40, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {zones[zone].label}
        </UI_CAPTION_SEMIBOLD_DF>
        <TooltipV2 text={zones[zone].hint} placement="right" wrapperStyle={{ display: "inline-flex" }}>
          <Info size={13} color={colors2.grey40} style={{ display: "block", cursor: "pointer" }} />
        </TooltipV2>
      </ZoneTag>
    </foreignObject>
  );
};

const axisTick = { fill: _base_linechart_ui_configs.xAxis.tickFill, fontSize: 11, fontWeight: 500, fontFamily: TICK_FONT_FAMILY };
const axisLine = { stroke: _base_linechart_ui_configs.xAxis.axisLineStroke };

// The bubble. recharts hands the point's pixel centre + its datum; hover and
// click are wired here so the chart needs no recharts Tooltip of its own.
const Bubble = (props) => {
  const { cx, cy, payload, onHover, onLeave, onSelect, selectedId } = props;
  if (cx == null || cy == null || !payload) return null;
  const { r, color, hollow, dimmed, faded, item } = payload;
  const selected = selectedId && item.nodeId === selectedId;
  return (
    <circle
      className={["triage-dot", dimmed && "triage-dim", faded && "triage-faded"].filter(Boolean).join(" ")}
      cx={cx}
      cy={cy}
      r={r}
      fill={hollow ? colors2.white : color}
      stroke={selected ? colors2.grey80 : hollow ? color : faded ? "none" : colors2.white}
      strokeWidth={selected ? 2.5 : hollow ? 1.5 : faded ? 0 : 2}
      strokeDasharray={hollow ? "3 2" : undefined}
      onMouseEnter={() => onHover(item, color)}
      onMouseLeave={onLeave}
      onClick={() => onSelect(item)}
    />
  );
};

// A direct label, drawn by a second Scatter whose points carry the label's
// own (already de-overlapped) y so recharts places it for us. A thin leader
// runs from the bubble's edge (its own y, `bubbleDyPx` below or above the
// label) to the text, so a pushed-apart label still points at its bubble.
const LEADER_PX = 14;
const LABEL_PAD_X = 4;
// The plate hugs the glyphs, not the text box (which carries line-height air).
const LABEL_PLATE_H = 15;
const BubbleLabel = (props) => {
  const { cx, cy, payload } = props;
  const textRef = useRef(null);
  // The text's box, measured once drawn, sizes the translucent plate behind it.
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    if (!textRef.current) return;
    const b = textRef.current.getBBox();
    setBox((prev) => (prev && prev.width === b.width ? prev : { width: b.width }));
  }, [payload?.item?.label]);
  if (cx == null || cy == null || !payload) return null;
  const { r, item, labelRight, bubbleDyPx = 0 } = payload;
  const dir = labelRight ? 1 : -1;
  const bubbleCy = cy + bubbleDyPx;
  const x0 = cx + dir * (r + 2);
  const x1 = cx + dir * (r + LEADER_PX);
  const textX = x1 + dir * 4;
  return (
    <g className="triage-label">
      <line x1={x0} y1={bubbleCy} x2={x1} y2={cy} stroke={colors2.grey30} strokeWidth={1} />
      {box && (
        <rect
          x={(labelRight ? textX : textX - box.width) - LABEL_PAD_X}
          y={cy - LABEL_PLATE_H / 2}
          width={box.width + LABEL_PAD_X * 2}
          height={LABEL_PLATE_H}
          rx={4}
          fill={colors2.white}
          fillOpacity={0.7}
        />
      )}
      <text ref={textRef} x={textX} y={cy + 4} fontSize={11.5} fontWeight={500} fill={colors2.grey80} textAnchor={labelRight ? "start" : "end"}>
        {item.label}
      </text>
    </g>
  );
};

// `labelIds`: exactly which bubbles carry a direct label (a Set of nodeIds),
// replacing the zone-based pick; unlike `highlightIds` it dims nothing.
export const TriageField = ({ items, meta, titleByContentType, onRowClick, openRowNodeId, highlightIds = null, labelIds = null, fill = false, focusIds = null, blurNote, blurNoteIcon }) => {
  const categoryTheme = useSignalCategories();
  const zoneCopy = resolveZones(categoryTheme.prioritiesCopy);
  const wrapperRef = useRef(null);
  const { mousePosition, isHovering } = useTrackMousePosition({ containerRef: wrapperRef });
  // In fill mode the wrapper's height is the chart's; until measured, the fixed one.
  const [measuredHeight, setMeasuredHeight] = useState(null);
  useEffect(() => {
    if (!fill || !wrapperRef.current) return;
    const ro = new ResizeObserver(([entry]) => setMeasuredHeight(Math.floor(entry.contentRect.height)));
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [fill]);
  const height = fill && measuredHeight ? measuredHeight : HEIGHT;
  const [hovered, setHovered] = useState(null);

  const { points, labelPoints, yMax } = useMemo(() => {
    const maxSev = items.reduce((m, i) => Math.max(m, i.severity.value), 0);
    const yMaxValue = Math.max(2.5, Math.ceil(maxSev * 1.1 * 10) / 10);

    // Direct labels on the actionable zones (fires, resolving, emerging —
    // the ones a PM reads first) plus the most severe chronic ones; the
    // rest name themselves on hover.
    const labelled = new Set();
    const pick = (zone, max) =>
      items
        .filter((i) => i.zone === zone)
        .sort((a, b) => b.severity.value - a.severity.value)
        .slice(0, max)
        .forEach((i) => labelled.add(i.nodeId));
    pick("fires", 4);
    pick("resolving", 3);
    pick("emerging", 3);
    pick("chronic", 3);
    // An insight in focus names exactly which bubbles matter: label those,
    // and only those, while it holds.
    if (highlightIds) {
      labelled.clear();
      highlightIds.forEach((id) => labelled.add(id));
    }
    if (labelIds) {
      labelled.clear();
      labelIds.forEach((id) => labelled.add(id));
    }

    const maxN = items.reduce((acc, item) => Math.max(acc, item.n || 0), 0);
    const pts = items
      .map((item) => {
        // Plot position, not the index: linear inside the band, compressed
        // beyond it (xScale.js). The tooltip still reads the real index.
        const x = item.momentum.available ? plotX(item.momentum.index) : 0;
        return {
          x,
          y: clamp(item.severity.value, 0, yMaxValue),
          r: radiusOf(item.n, maxN),
          color: item.category ? categoryTheme.accentOf(item.category) : colors2.grey60,
          hollow: !item.momentum.available,
          dimmed: !!highlightIds && !highlightIds.has(item.nodeId),
          faded: !!focusIds && !focusIds.has(item.nodeId),
          labelled: labelled.has(item.nodeId),
          // Labels sit to the right unless the bubble is near the right edge.
          labelRight: x < PLOT_X_MAX * 0.55,
          item,
        };
      })
      // Big bubbles first so small ones stay hoverable on top.
      .sort((a, b) => b.r - a.r);

    // Labels on the same side are pushed apart top-down so two bubbles at
    // nearly the same severity don't print over each other. The gap is
    // converted from pixels into severity units with the plot's height.
    const pxPerUnit = plotHeightPx(height) / yMaxValue;
    const gap = LABEL_GAP_PX / pxPerUnit;
    const labels = pts.filter((p) => p.labelled).map((p) => ({ ...p, y: p.y, bubbleY: p.y }));
    for (const side of [true, false]) {
      const group = labels.filter((l) => l.labelRight === side).sort((a, b) => b.y - a.y);
      for (let k = 1; k < group.length; k += 1) {
        if (group[k - 1].y - group[k].y < gap) group[k].y = group[k - 1].y - gap;
      }
    }
    // Where the bubble sits relative to its label, in pixels (y grows downward).
    for (const l of labels) l.bubbleDyPx = (l.y - l.bubbleY) * pxPerUnit;
    return { points: pts, labelPoints: labels, yMax: yMaxValue };
  }, [items, categoryTheme, highlightIds, labelIds, focusIds, height]);

  const onHover = useCallback((item, color) => setHovered({ item, color }), []);
  const onLeave = useCallback(() => setHovered(null), []);
  const onSelect = useCallback((item) => onRowClick?.(item.row), [onRowClick]);

  // One stable element for the shape prop — recharts clones it per point
  // with cx/cy/payload; our handlers ride along as extra props.
  const bubble = useMemo(
    () => <Bubble onHover={onHover} onLeave={onLeave} onSelect={onSelect} selectedId={openRowNodeId} />,
    [onHover, onLeave, onSelect, openRowNodeId]
  );

  const xMax = PLOT_X_MAX;
  const xEdge = xMax + X_OVERSHOOT;
  const high = CONFIG.SEVERITY_HIGH;
  const zones = [
    ["resolving", -xEdge, -1, high, yMax, "insideTopLeft"],
    ["chronic", -1, 1, high, yMax, "insideTopLeft"],
    ["fires", 1, xEdge, high, yMax, "insideTopLeft"],
    ["fading", -xEdge, -1, 0, high, "insideBottomLeft"],
    ["background", -1, 1, 0, high, "insideBottomLeft"],
    ["emerging", 1, xEdge, 0, high, "insideBottomLeft"],
  ];
  // The end ticks carry the direction words; the middle ones print the
  // INDEX the tick stands for (1, 2, 4, 8), not the plot position. The
  // band edges (±1) and zero read in the axis colour; the doublings beyond
  // them are a lighter grey so they scale the axis without competing.
  const xTickFormatter = (v) => (v === -xMax ? "← cooling" : v === xMax ? "rising →" : String(unplotX(v)));
  const isMinorTick = (v) => Math.abs(v) > 1 && Math.abs(v) < xMax;
  const xTick = ({ x, y, payload }) => (
    <text
      x={x}
      y={y}
      dy="0.71em"
      textAnchor="middle"
      fill={isMinorTick(payload.value) ? colors2.grey30 : axisTick.fill}
      fontSize={axisTick.fontSize}
      fontWeight={axisTick.fontWeight}
      fontFamily={axisTick.fontFamily}
    >
      {xTickFormatter(payload.value)}
    </text>
  );

  const tooltipDetails = hovered
    ? [
        `${hovered.item.signal?.display || "no signal"} · severity ${severityText(hovered.item)}`,
        `${momentumText(hovered.item, meta).arrow} ${momentumText(hovered.item, meta).text}`,
        `${hovered.item.n} observation${hovered.item.n === 1 ? "" : "s"} · ${sourcesText(hovered.item, titleByContentType)}${
          hovered.item.zone ? ` · ${zoneCopy[hovered.item.zone].label}` : ""
        }`,
      ]
    : [];

  return (
    <DashboardWidgetShell
      titleJSX={<UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey80 }}>Where to focus</UI_BODY_SEMIBOLD_SM_DF>}
      style={{ minHeight: 0, border: "none", ...(fill ? { height: "100%", boxSizing: "border-box" } : {}) }}
      bodyStyle={fill ? { display: "flex", flexDirection: "column", minHeight: 0 } : undefined}
    >
      <Wrap ref={wrapperRef} $fill={fill} $momentumBlurred={!!blurNote}>
        <ResponsiveContainer width="100%" height={height}>
          <ScatterChart margin={MARGIN}>
            {zones.map(([zone, x1, x2, y1, y2, labelPos]) => (
              <ReferenceArea
                key={zone}
                x1={x1}
                x2={x2}
                y1={y1}
                y2={y2}
                fill={zoneCopy[zone].field}
                fillOpacity={1}
                stroke="none"
                ifOverflow="hidden"
                label={<ZoneLabel zone={zone} atBottom={labelPos === "insideBottomLeft"} />}
              />
            ))}

            <ReferenceLine y={high} stroke={colors2.grey15} />
            <ReferenceLine x={-1} stroke={colors2.grey15} className="momentum-line" />
            <ReferenceLine x={1} stroke={colors2.grey15} className="momentum-line" />
            <ReferenceLine x={0} stroke={colors2.grey10} strokeDasharray="3 4" className="momentum-line" />

            <XAxis
              type="number"
              dataKey="x"
              domain={[-xEdge, xEdge]}
              ticks={PLOT_X_TICKS}
              tickFormatter={xTickFormatter}
              tickLine={false}
              axisLine={axisLine}
              tick={xTick}
              tickMargin={8}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={[0, yMax]}
              ticks={[0, high, yMax]}
              tickLine={false}
              axisLine={axisLine}
              tick={axisTick}
              width={40}
              label={{
                value: "severity",
                angle: -90,
                position: "insideLeft",
                offset: 10,
                fill: colors2.grey50,
                fontSize: 11,
                fontFamily: TICK_FONT_FAMILY,
              }}
            />

            <Scatter data={points} shape={bubble} isAnimationActive={false} />
            {/* Labels ride in their own layer, above every bubble. */}
            <Scatter data={labelPoints} shape={<BubbleLabel />} isAnimationActive={false} />
          </ScatterChart>
        </ResponsiveContainer>

        {blurNote && (
          <NoteOnAxis>
            <BlurNotePill note={blurNote} icon={blurNoteIcon} />
          </NoteOnAxis>
        )}

        {isHovering && hovered && (
          <TooltipV2
            controlled
            isOpen
            mousePosition={mousePosition}
            primaryContent={{
              layout: "custom",
              render: (
                <TooltipContent
                  type="scatter"
                  data={{ tooltipLabel: hovered.item.label, barColor: hovered.color, details: tooltipDetails }}
                />
              ),
            }}
          />
        )}
      </Wrap>
    </DashboardWidgetShell>
  );
};
